import Badge from "@cloudscape-design/components/badge"
import Box from "@cloudscape-design/components/box"
import Button from "@cloudscape-design/components/button"
import Container from "@cloudscape-design/components/container"
import Grid from "@cloudscape-design/components/grid"
import Header from "@cloudscape-design/components/header"
import Icon from "@cloudscape-design/components/icon"
import Link from "@cloudscape-design/components/link"
import Select, { type SelectProps } from "@cloudscape-design/components/select"
import SpaceBetween from "@cloudscape-design/components/space-between"
import Spinner from "@cloudscape-design/components/spinner"
import StatusIndicator from "@cloudscape-design/components/status-indicator"
import Table, { type TableProps } from "@cloudscape-design/components/table"
import TextFilter from "@cloudscape-design/components/text-filter"
import TruncatedText from "@cloudscape-design/components/truncated-text"
import { useEffect, useMemo, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import type { Study, StudyAttention } from "../../services/daeApi"
import Breadcrumb from "../../components/Navigation/Breadcrumb"
import Pagination from "../../components/Pagination/Pagination"
import { useAuth } from "../../contexts/AuthContext"
import { useDashboardStats, useStudiesNeedingAttention } from "../../hooks/queries/useDashboard"
import { useStudiesList } from "../../hooks/queries/useStudies"
import { usePageParam } from "../../hooks/usePageParam"

const ITEMS_PER_PAGE = 20

// Derived (non-status) filter value: studies with a failed / partially-completed ingestion run or
// quarantined records. This is NOT a study_status — it is the same actionable "needs attention"
// signal shown by the dashboard tile, sourced from the shared useStudiesNeedingAttention hook (one
// definition). This list is intersected with the studies loaded on this page, so it converges to
// the tile count once every study page has loaded. Kept as a special value in the same status
// dropdown so it stays a discoverable, URL-shareable filter (the dashboard tile deep-links to
// ?status=NEEDS_ATTENTION).
const NEEDS_ATTENTION_FILTER = "NEEDS_ATTENTION"

// Short, human-readable summary of why a study needs attention, for the Attention column.
// Only ever called for flagged studies, so at least one clause is always present.
const attentionHint = ({ failedRuns, quarantinedRecords }: StudyAttention): string => {
  const parts: string[] = []
  if (failedRuns > 0) parts.push(`${failedRuns} failed run${failedRuns === 1 ? "" : "s"}`)
  if (quarantinedRecords > 0) {
    parts.push(`${quarantinedRecords} quarantined record${quarantinedRecords === 1 ? "" : "s"}`)
  }
  return parts.join(" · ")
}

const STATUS_OPTIONS: SelectProps.Option[] = [
  { label: "All Status", value: "all" },
  { label: "Not Started", value: "NOT_STARTED" },
  { label: "Active", value: "ACTIVE" },
  { label: "On Hold", value: "ON_HOLD" },
  { label: "Completed", value: "COMPLETED" },
  { label: "Onboarded", value: "ONBOARDED" },
  { label: "Action Needed", value: "ACTION_NEEDED" },
  { label: "Needs Attention (data issues)", value: NEEDS_ATTENTION_FILTER },
]

type BadgeColor = React.ComponentProps<typeof Badge>["color"]

const STATUS_COLOR: Record<string, BadgeColor> = {
  NOT_STARTED: "grey",
  ACTIVE: "green",
  ON_HOLD: "severity-medium",
  COMPLETED: "green",
  ONBOARDED: "blue",
  ACTION_NEEDED: "red",
}

const MyStudies = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const daeId = user?.principalId

  const { data: dashboardInfo } = useDashboardStats(daeId)
  const { data: studiesNeedingAttention, isLoading: attentionLoading } =
    useStudiesNeedingAttention(daeId)

  // The status filter is URL-driven (via ?status=) so it is shareable and deep-linkable — e.g.
  // the DAE dashboard stat cards link straight to a filtered view.
  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = searchParams.get("status") ?? "all"
  const setStatusFilter = (value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (value === "all") {
          next.delete("status")
        } else {
          next.set("status", value)
        }
        next.delete("page")
        return next
      },
      { replace: true },
    )
  }

  // Filters and search
  const [searchTerm, setSearchTermRaw] = useState("")
  const [sorting, setSorting] = useState<TableProps.SortingState<Study>>({
    sortingColumn: { sortingField: "creation_date" },
    isDescending: true,
  })

  // Client-side page index over the filtered/sorted collection
  const [currentPage, setCurrentPage] = usePageParam()

  const setSearchTerm = (next: string) => {
    setSearchTermRaw(next)
    setCurrentPage(1)
  }

  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isRefetching,
    refetch,
  } = useStudiesList(daeId, ITEMS_PER_PAGE)

  // Auto-fetch remaining pages in the background
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // Flatten all fetched pages into a single collection
  const allStudies = useMemo(() => data?.pages.flatMap((p) => p.studies) ?? [], [data])

  // Stats derived from the full collection
  const stats = useMemo(() => {
    const total = dashboardInfo?.studies.total ?? 0
    const active = dashboardInfo?.studies.active ?? 0
    return {
      totalStudies: total,
      activeStudies: active,
    }
  }, [dashboardInfo])

  // Filter and sort across the ENTIRE collection. Sorting is driven by the Cloudscape Table
  const filteredStudies = useMemo(() => {
    const { sortingColumn, isDescending } = sorting
    const comparator = sortingColumn.sortingComparator
    const field = sortingColumn.sortingField as keyof Study | undefined
    const direction = isDescending ? -1 : 1
    return allStudies
      .filter((study) => {
        const matchesSearch =
          searchTerm === "" ||
          study.study_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          study.study_name.toLowerCase().includes(searchTerm.toLowerCase())
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === NEEDS_ATTENTION_FILTER
            ? (studiesNeedingAttention?.has(study.study_id) ?? false)
            : study.study_status === statusFilter)
        return matchesSearch && matchesStatus
      })
      .sort((a, b) => {
        let comparison: number
        if (comparator) {
          comparison = comparator(a, b)
        } else if (!field) {
          comparison = 0
        } else {
          const aVal = a[field] ?? ""
          const bVal = b[field] ?? ""
          comparison = aVal < bVal ? -1 : aVal > bVal ? 1 : 0
        }
        return comparison * direction
      })
  }, [allStudies, searchTerm, statusFilter, sorting, studiesNeedingAttention])

  // Client-side page slice over the filtered results
  // Use server-side total for page count (instant), fall back to loaded collection size
  const isFiltering = statusFilter !== "all" || searchTerm !== ""
  const totalForPagination = isFiltering
    ? filteredStudies.length
    : (dashboardInfo?.studies.total ?? filteredStudies.length)
  const pageCount = Math.max(1, Math.ceil(totalForPagination / ITEMS_PER_PAGE))
  const paginatedStudies = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE
    return filteredStudies.slice(start, start + ITEMS_PER_PAGE)
  }, [filteredStudies, currentPage])

  const handleRefresh = () => {
    setCurrentPage(1)
    void refetch()
  }

  const columnDefinitions: TableProps.ColumnDefinition<Study>[] = useMemo(
    () => [
      {
        id: "study_id",
        header: "Study ID",
        sortingField: "study_id",
        cell: (study) => (
          <Link
            href={`/studies/${study.study_id}`}
            ariaLabel={`View details for study ${study.study_id}`}
            onFollow={(e) => {
              e.preventDefault()
              navigate(`/studies/${study.study_id}`)
            }}
          >
            {study.study_id}
          </Link>
        ),
      },
      {
        id: "study_name",
        header: "Study Name",
        sortingField: "study_name",
        maxWidth: 360,
        cell: (study) => (
          <TruncatedText>
            {study.study_name || study.study_description || study.study_id}
          </TruncatedText>
        ),
      },
      {
        id: "study_status",
        header: "Status",
        sortingField: "study_status",
        minWidth: 140,
        cell: (study) => (
          <Badge color={STATUS_COLOR[study.study_status] ?? "grey"}>
            {study.study_status.replace(/_/g, " ")}
          </Badge>
        ),
      },
      {
        id: "attention",
        header: "Attention",
        minWidth: 200,
        cell: (study) => {
          const attention = studiesNeedingAttention?.get(study.study_id)
          if (!attention) {
            return <Box color="text-status-inactive">—</Box>
          }
          // Links straight to the study's ingestion tab (run history), where the failed runs and
          // quarantined records — and the reason each broke — are shown per record.
          return (
            <StatusIndicator type="warning">
              <Link
                href={`/studies/${study.study_id}?tab=ingestion`}
                ariaLabel={`View ingestion issues for study ${study.study_id}`}
                onFollow={(e) => {
                  e.preventDefault()
                  navigate(`/studies/${study.study_id}?tab=ingestion`)
                }}
              >
                {attentionHint(attention)}
              </Link>
            </StatusIndicator>
          )
        },
      },
      {
        id: "dta_count",
        header: "# DTAs",
        sortingField: "dta_count",
        sortingComparator: (a, b) => (a.dta_count ?? 0) - (b.dta_count ?? 0),
        cell: (study) => study.dta_count ?? 0,
      },
      {
        id: "vendor_count",
        header: "# Vendors",
        sortingField: "vendor_count",
        sortingComparator: (a, b) =>
          (a.vendor_ids ? new Set(a.vendor_ids).size : 0) -
          (b.vendor_ids ? new Set(b.vendor_ids).size : 0),
        cell: (study) => (study.vendor_ids ? new Set(study.vendor_ids).size : 0),
      },
      {
        id: "actions",
        header: "Actions",
        minWidth: 240,
        cell: (study) => (
          <div style={{ display: "flex", gap: "12px", whiteSpace: "nowrap" }}>
            <Link
              href={`/studies/${study.study_id}?tab=dtas`}
              onFollow={(e) => {
                e.preventDefault()
                navigate(`/studies/${study.study_id}?tab=dtas`)
              }}
            >
              View DTAs
            </Link>
            <Link
              href={`/studies/${study.study_id}?action=create`}
              onFollow={(e) => {
                e.preventDefault()
                navigate(`/studies/${study.study_id}?action=create`)
              }}
            >
              Create DTA
            </Link>
          </div>
        ),
      },
    ],
    [navigate, studiesNeedingAttention],
  )

  const selectedStatus = STATUS_OPTIONS.find((o) => o.value === statusFilter) ?? STATUS_OPTIONS[0]

  return (
    <SpaceBetween size="l">
      <Breadcrumb items={[{ label: "All Studies" }]} />

      <Header
        variant="h1"
        description="Manage the clinical studies you have access to and their data ingestion"
      >
        My Studies
      </Header>

      {error && (
        <Box color="text-status-error">
          <SpaceBetween size="xxs">
            <Box fontWeight="bold">Unable to Load Studies</Box>
            <Box>Failed to load studies. Please try again later.</Box>
          </SpaceBetween>
        </Box>
      )}

      {/* Quick Stats */}
      <Grid gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}>
        <Container>
          <SpaceBetween direction="horizontal" size="s">
            <Box color="text-status-info">
              <Icon name="folder" size="medium" />
            </Box>
            <Box>
              <Box color="text-body-secondary" fontSize="body-s">
                Total Studies
              </Box>
              <Box fontSize="heading-l" fontWeight="bold">
                {!dashboardInfo ? <Spinner /> : stats.totalStudies}
              </Box>
            </Box>
          </SpaceBetween>
        </Container>
        <Container>
          <SpaceBetween direction="horizontal" size="s">
            <Box color="text-status-success">
              <Icon name="status-positive" size="medium" />
            </Box>
            <Box>
              <Box color="text-body-secondary" fontSize="body-s">
                Active Studies
              </Box>
              <Box fontSize="heading-l" fontWeight="bold">
                {!dashboardInfo ? <Spinner /> : stats.activeStudies}
              </Box>
            </Box>
          </SpaceBetween>
        </Container>
      </Grid>

      <Container>
        <Table
          variant="embedded"
          contentDensity="comfortable"
          columnDefinitions={columnDefinitions}
          items={paginatedStudies}
          loading={
            isLoading ||
            // The Needs Attention filter depends on the attention fan-out; show the spinner while
            // it resolves so a deep-link to ?status=NEEDS_ATTENTION doesn't flash an empty table.
            (statusFilter === NEEDS_ATTENTION_FILTER && attentionLoading) ||
            (paginatedStudies.length === 0 && (hasNextPage || isFetchingNextPage))
          }
          loadingText="Loading studies..."
          trackBy="study_id"
          ariaLabels={{ tableLabel: "Assigned studies" }}
          sortingColumn={sorting.sortingColumn}
          sortingDescending={sorting.isDescending}
          onSortingChange={({ detail }) => setSorting(detail)}
          header={
            <Header
              counter={`(${stats.totalStudies})`}
              actions={
                <Button iconName="refresh" onClick={handleRefresh} disabled={isRefetching}>
                  Refresh
                </Button>
              }
            >
              Your Studies
            </Header>
          }
          filter={
            <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
              <div style={{ flexGrow: 1, maxWidth: 480 }}>
                <TextFilter
                  filteringText={searchTerm}
                  filteringPlaceholder="Search by Study ID or Name..."
                  filteringAriaLabel="Search studies"
                  onChange={({ detail }) => setSearchTerm(detail.filteringText)}
                />
              </div>
              <div style={{ minWidth: 180 }}>
                <Select
                  selectedOption={selectedStatus}
                  options={STATUS_OPTIONS}
                  ariaLabel="Filter by status"
                  onChange={({ detail }) => setStatusFilter(detail.selectedOption.value ?? "all")}
                />
              </div>
            </div>
          }
          empty={
            <Box textAlign="center" color="inherit">
              No studies found
            </Box>
          }
          pagination={
            <Pagination
              currentPage={currentPage}
              pageCount={pageCount}
              loading={isLoading}
              onPageChange={setCurrentPage}
              onNext={() => setCurrentPage(currentPage + 1)}
              onPrevious={() => setCurrentPage(currentPage - 1)}
            />
          }
        />
      </Container>
    </SpaceBetween>
  )
}

export default MyStudies
